/**
 * Candidates for one slot: the same band, from this client's own earlier runs.
 *
 * This is a regression-repair list, so the ordering is not chronological
 * vanity - a pinned favorite is someone saying "this one was right", and it
 * goes first. Everything else follows newest first, because the run you are
 * repairing is usually the newest one and the one before it is the usual fix.
 *
 * ⚠ ONE CLIENT ONLY. A band carries its client's city, phone, claims and form
 * shortcode with it, so a candidate from another tenant is not a worse
 * candidate, it is not a candidate. `buildCandidates` is given one tenant's
 * drafts and drops any favorite whose source tenant differs, and it reports
 * how many it dropped rather than pretending they did not exist.
 */
import { applyBandSnapshot, listBands, snapshotBand } from './scopeBand';

export const candidateNamespace = (draftId: string, bandId: string): string =>
  `fav-${draftId.slice(0, 8)}-${bandId}`.replace(/[^a-z0-9-]/gi, '-');

export type DraftSource = {
  draftId: string;
  createdAt: string;
  label: string;
  /** Loaded page HTML, or null while it has not been fetched yet. */
  html: string | null;
  /** Why this draft could not be loaded, if it could not. */
  error: string | null;
};

export type CandidatePin = { favoriteId: string; name: string };

export type Candidate = {
  draftId: string;
  createdAt: string;
  label: string;
  pin: CandidatePin | null;
  /** `ready` can be previewed and swapped; anything else says why it cannot. */
  state: 'ready' | 'loading' | 'unresolved';
  reason: string | null;
  detail?: string;
};

export type CandidateList = {
  candidates: Candidate[];
  /** Favorites pinned from a different client, excluded from this build. */
  otherClientPins: number;
};

export function buildCandidates(args: {
  bandId: string;
  tenantId: string;
  /** When supplied, readiness includes a preview-only composition into this exact page. */
  destinationHtml?: string;
  /** Every draft of this tenant except the one being repaired. */
  drafts: readonly DraftSource[];
  favorites: readonly {
    id: string;
    name: string;
    semantic_role: string;
    source_draft_id: string;
    source_tenant_id: string;
  }[];
}): CandidateList {
  const { bandId, tenantId, drafts, favorites } = args;
  const wanted = bandId.trim().toLowerCase();

  const mine = favorites.filter((f) => f.semantic_role?.trim().toLowerCase() === wanted);
  const otherClientPins = mine.filter((f) => f.source_tenant_id !== tenantId).length;
  const pinByDraft = new Map<string, CandidatePin>();
  for (const favorite of mine) {
    if (favorite.source_tenant_id !== tenantId) continue;
    if (!pinByDraft.has(favorite.source_draft_id)) {
      pinByDraft.set(favorite.source_draft_id, { favoriteId: favorite.id, name: favorite.name });
    }
  }

  const candidates = drafts.map((draft): Candidate => {
    const pin = pinByDraft.get(draft.draftId) ?? null;
    const base = { draftId: draft.draftId, createdAt: draft.createdAt, label: draft.label, pin };
    if (draft.error) {
      return { ...base, state: 'unresolved', reason: draft.error };
    }
    if (draft.html === null) {
      return { ...base, state: 'loading', reason: null };
    }
    if (!listBands(draft.html).includes(wanted)) {
      return { ...base, state: 'unresolved', reason: `this run did not ship a ${wanted} band` };
    }
    if (args.destinationHtml !== undefined) {
      const snap = snapshotBand({ doc: draft.html, bandId, tenantId, namespace: candidateNamespace(draft.draftId, bandId) });
      const result = snap.ok
        ? applyBandSnapshot({ doc: args.destinationHtml, bandId, tenantId, snapshot: snap.snapshot })
        : snap;
      if (!result.ok) return { ...base, state: 'unresolved', reason: result.reason, detail: result.detail };
    }
    return { ...base, state: 'ready', reason: null };
  });

  candidates.sort((a, b) => {
    if (Boolean(a.pin) !== Boolean(b.pin)) return a.pin ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });

  return { candidates, otherClientPins };
}
